import axios from "axios";
import { publicApi } from "../api";
import {
  BillingReceiptError,
  BillingReceiptItem,
  OrderReceiptData,
} from "../../interfaces/billing-order-details.interface";

const value = (...values: unknown[]) => values.find((item) => item !== null && item !== undefined && item !== "");
const text = (item: unknown) => typeof item === "string" || typeof item === "number" ? String(item).trim() || undefined : undefined;
const number = (item: unknown) => Number.isFinite(Number(item)) ? Number(item) : 0;
const array = (item: unknown): any[] => Array.isArray(item) ? item : [];

const mapItem = (item: any): BillingReceiptItem => ({
  name: text(value(item?.name, item?.productName, item?.Name)) || "Product",
  imageUrl: text(value(item?.imageUrl, item?.image, item?.productImageUrl)),
  quantity: number(value(item?.quantity, item?.qty)),
  unit: text(value(item?.unit, item?.measurementTypeCode, item?.unitName)),
  mrp: number(value(item?.mrp, item?.MRP)),
  sellingPrice: number(value(item?.sellingPrice, item?.unitPrice, item?.price)),
  discountAmount: number(value(item?.discountAmount, item?.discount)),
  lineTotal: number(value(item?.lineTotal, item?.totalAmount, item?.amount)),
});

const mapOrderReceipt = (payload: any): OrderReceiptData => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const summary = data?.summary || {};
  const payment = data?.payment || {};
  const items = array(data?.items || data?.orderItems || data?.products).map(mapItem);

  return {
    receiptType: "order",
    orderNumber: text(value(data?.orderNumber, data?.orderNo, data?.orderID, data?.orderId)) || "",
    orderDate: text(value(data?.orderDate, data?.createdDate, data?.date)),
    customer: {
      name: text(value(data?.customer?.name, data?.customerName, data?.userName)),
      maskedMobile: text(value(data?.customer?.maskedMobile, data?.maskedMobile, data?.primaryContactNbrMasked)),
    },
    store: {
      name: text(value(data?.store?.name, data?.storeName, data?.pos?.name)),
      address: text(value(data?.store?.address, data?.storeAddress, data?.pos?.address)),
    },
    payment: {
      mode: text(value(payment.mode, data?.paymentMode, data?.paymentModeName)),
      status: text(value(payment.status, data?.paymentStatus)),
      displayText: text(value(payment.displayText, payment.label)),
    },
    summary: {
      itemCount: number(value(summary.itemCount, data?.itemCount, items.length)),
      totalUnits: number(value(summary.totalUnits, data?.totalUnits, items.reduce((total, item) => total + item.quantity, 0))),
      taxableAmount: number(value(summary.taxableAmount, data?.taxableAmount)),
      discountAmount: number(value(summary.discountAmount, data?.discountAmount)),
      couponDiscountAmount: number(value(summary.couponDiscountAmount, data?.couponDiscountAmount)),
      rewardPointDiscountAmount: number(value(summary.rewardPointDiscountAmount, data?.rewardPointDiscountAmount)),
      deliveryCharge: number(value(summary.deliveryCharge, data?.deliveryCharge, data?.courierCharges)),
      taxAmount: number(value(summary.taxAmount, data?.taxAmount)),
      paymentGatewayCharges: number(value(summary.paymentGatewayCharges, data?.paymentGatewayCharges)),
      grossAmount: number(value(summary.grossAmount, data?.grossAmount, data?.totalAmount)),
      rewardPointsEarned: number(value(summary.rewardPointsEarned, data?.rewardPointsEarned)),
      totalAmountPaid: number(summary.totalAmountPaid),
    },
    items,
  };
};

export const getOrderReceipt = async (orderNumber: string) => {
  try {
    const response = await publicApi.get(`/api/public/receipt/${encodeURIComponent(`order-${orderNumber}`)}`, {
      headers: { Accept: "application/json" },
    });
    const body = response.data?.data && typeof response.data.data === "object" ? response.data.data : response.data;
    if (body?.receiptType !== "order") throw new BillingReceiptError("not-found");
    const receipt = mapOrderReceipt(response.data);
    if (!receipt.orderNumber) throw new BillingReceiptError("not-found");
    if (body?.summary?.totalAmountPaid === undefined || body.summary.totalAmountPaid === null) throw new BillingReceiptError("error");
    return receipt;
  } catch (error) {
    if (error instanceof BillingReceiptError) throw error;
    if (axios.isAxiosError(error) && [400, 404].includes(error.response?.status || 0)) {
      throw new BillingReceiptError("not-found");
    }
    throw new BillingReceiptError("error");
  }
};